import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { analyticsOverviewService } from "@/services/analytics-overview-service";
import { analyticsRevenueService } from "@/services/analytics-revenue-service";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useOverviewKpiPeriodComparison } from "../hooks/useOverviewKpiPeriodComparison";
import { useOverviewAnalytics } from "../hooks/useOverviewAnalytics";
import { useRevenueTotal } from "../hooks/useRevenueTotal";
import { downloadBlob } from "@/utils/downloadFile";
import { OverviewAreaChart } from "./overview/OverviewAreaChart";
import { GuestPathSection } from "./overview/GuestPathSection";
import { OverviewKpiCards } from "./overview/OverviewKpiCards";
import {
  downloadGuestPathPreCheckInExport,
  downloadGuestPathPreCheckOutExport,
} from "./overview/guestPathExport";
import { mapOverviewMetricsToAreaChartData } from "./overview/overviewAreaChartMappers";
import { buildOverviewKpiMetrics } from "./overview/overviewKpiMappers";

export const OverviewTab: React.FC = () => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const overview = useOverviewAnalytics(dateRange);
  const revenueTotal = useRevenueTotal(dateRange);
  const periodComparison = useOverviewKpiPeriodComparison(dateRange);

  const kpiMetrics = useMemo(
    () =>
      buildOverviewKpiMetrics({
        offerConversion: overview.offerConversion.data,
        ratingsByStay: overview.ratingsByStay.data,
        ordersByDay: overview.ordersByDay.data,
        revenueTotal: revenueTotal.data,
        previousOfferConversion: periodComparison.offerConversion.data,
        previousRatingsByStay: periodComparison.ratingsByStay.data,
        previousOrdersByDay: periodComparison.ordersByDay.data,
        previousRevenueTotal: periodComparison.revenueTotal.data,
        includeDeltas:
          overview.isReady &&
          periodComparison.previousRange !== null &&
          !periodComparison.isLoading,
      }),
    [
      overview.offerConversion.data,
      overview.ratingsByStay.data,
      overview.ordersByDay.data,
      overview.isReady,
      revenueTotal.data,
      periodComparison.offerConversion.data,
      periodComparison.ratingsByStay.data,
      periodComparison.ordersByDay.data,
      periodComparison.revenueTotal.data,
      periodComparison.isLoading,
      periodComparison.previousRange,
    ],
  );

  const isKpiLoading =
    !overview.isReady || overview.isLoading || revenueTotal.isLoading;

  const isKpiComparisonLoading =
    periodComparison.previousRange !== null && periodComparison.isLoading;

  const areaChartData = useMemo(
    () =>
      mapOverviewMetricsToAreaChartData({
        revenue: revenueTotal.data,
        offerConversion: overview.offerConversion.data,
        ratingsByStay: overview.ratingsByStay.data,
        ordersByDay: overview.ordersByDay.data,
      }),
    [
      overview.offerConversion.data,
      overview.ordersByDay.data,
      overview.ratingsByStay.data,
      revenueTotal.data,
    ],
  );

  const isAreaChartLoading =
    !overview.isReady ||
    revenueTotal.isLoading ||
    overview.offerConversion.isLoading ||
    overview.ratingsByStay.isLoading ||
    overview.ordersByDay.isLoading;

  const isAreaChartError =
    revenueTotal.isError ||
    overview.offerConversion.isError ||
    overview.ratingsByStay.isError ||
    overview.ordersByDay.isError;

  const canExport =
    overview.isReady &&
    !overview.isLoading &&
    !overview.isError &&
    !revenueTotal.isLoading &&
    !revenueTotal.isError;

  const handleExportOverview = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!canExport) {
        return;
      }

      const params = {
        hotelId: overview.hotelId,
        from: overview.from,
        to: overview.to,
        format,
      };

      try {
        const [offerConversion, ratingsByStay, ordersByDay, totalRevenue] =
          await Promise.all([
            analyticsOverviewService.exportOfferConversion(params),
            analyticsOverviewService.exportRatingsByStay(params),
            analyticsOverviewService.exportOrdersByDay(params),
            analyticsRevenueService.exportTotal(params),
          ]);

        downloadBlob(
          offerConversion,
          `overview-offer-conversion-${overview.hotelId}-${overview.from}-${overview.to}.${format}`,
        );
        downloadBlob(
          ratingsByStay,
          `overview-ratings-by-stay-${overview.hotelId}-${overview.from}-${overview.to}.${format}`,
        );
        downloadBlob(
          ordersByDay,
          `overview-orders-by-day-${overview.hotelId}-${overview.from}-${overview.to}.${format}`,
        );
        downloadBlob(
          totalRevenue,
          `overview-total-revenue-${overview.hotelId}-${overview.from}-${overview.to}.${format}`,
        );

        if (overview.preCheckInByDay.data) {
          downloadGuestPathPreCheckInExport({
            ...params,
            data: overview.preCheckInByDay.data,
          });
        }

        if (overview.preCheckOutByDay.data) {
          downloadGuestPathPreCheckOutExport({
            ...params,
            data: overview.preCheckOutByDay.data,
          });
        }

        toast.success(
          t(
            "analytics.overview.toast.exportSuccess",
            "Overview analytics export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.overview.toast.exportError",
            "Failed to export overview analytics",
          ),
        );
      }
    },
    [
      canExport,
      overview.from,
      overview.hotelId,
      overview.preCheckInByDay.data,
      overview.preCheckOutByDay.data,
      overview.to,
      t,
    ],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport,
      onExport: handleExportOverview,
    }),
    [canExport, handleExportOverview],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  return (
    <section className="space-y-6">
      <OverviewKpiCards
        metrics={kpiMetrics}
        isLoading={isKpiLoading}
        isComparisonLoading={isKpiComparisonLoading}
      />
      <OverviewAreaChart
        data={areaChartData}
        isLoading={isAreaChartLoading}
        isError={isAreaChartError}
      />
      <GuestPathSection
        preCheckIn={overview.preCheckInByDay.data}
        preCheckOut={overview.preCheckOutByDay.data}
        isLoading={
          !overview.isReady ||
          overview.preCheckInByDay.isLoading ||
          overview.preCheckOutByDay.isLoading
        }
        exportParams={{
          hotelId: overview.hotelId,
          from: overview.from,
          to: overview.to,
          isReady: overview.isReady,
        }}
      />
    </section>
  );
};
